import { useRef, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";

function getErrorMessage(error) {
  const detail = error?.response?.data?.detail;

  if (typeof detail === "string") return detail;

  if (Array.isArray(detail)) {
    return detail.map((item) => item.msg).join(". ");
  }

  return "No fue posible procesar el archivo Excel.";
}

export default function CompanyAccountsExcel({
  apiUrl,
  onImported,
}) {
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const inputRef = useRef(null);
  const operationLock = useRef(false);

  const baseUrl = apiUrl.replace(/\/+$/, "");

  const getConfig = () => ({
    headers: {
      Authorization: `Bearer ${localStorage.getItem(
        "company_token"
      )}`,
    },
  });

  const download = async (type) => {
    if (operationLock.current) return;

    operationLock.current = true;
    setBusy(true);

    try {
      const response = await axios.get(
        `${baseUrl}/accounting/accounts/excel/${type}`,
        {
          ...getConfig(),
          responseType: "blob",
        }
      );

      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");

      link.href = url;
      link.download =
        type === "template"
          ? "plantilla_cuentas.xlsx"
          : "plan_de_cuentas.xlsx";

      document.body.appendChild(link);
      link.click();
      link.remove();

      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      toast.error(
        "No fue posible descargar el Excel. Revisa tu sesión y permisos."
      );
    } finally {
      operationLock.current = false;
      setBusy(false);
    }
  };

  const selectFile = (event) => {
    const selected = event.target.files?.[0] || null;

    setResult(null);

    if (
      selected &&
      !selected.name.toLowerCase().endsWith(".xlsx")
    ) {
      toast.error("Selecciona un archivo .xlsx.");
      event.target.value = "";
      setFile(null);
      return;
    }

    if (selected && selected.size > 5 * 1024 * 1024) {
      toast.error("El archivo no debe superar 5 MB.");
      event.target.value = "";
      setFile(null);
      return;
    }

    setFile(selected);
  };

  const processFile = async (dryRun) => {
    if (!file || operationLock.current) return;

    if (!dryRun && !result?.valid) return;

    operationLock.current = true;
    setBusy(true);

    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await axios.post(
        `${baseUrl}/accounting/accounts/excel/import`,
        formData,
        {
          ...getConfig(),
          params: { dry_run: dryRun },
        }
      );

      const data = response.data;
      setResult(data);

      if (!data.valid) {
        toast.error(
          "Corrige los errores del archivo antes de importar."
        );
        return;
      }

      if (dryRun) {
        toast.success(
          `${data.total} cuentas listas para importar.`
        );
        return;
      }

      toast.success(
        `${data.imported} cuentas importadas correctamente.`
      );

      setFile(null);
      setResult(null);

      if (inputRef.current) {
        inputRef.current.value = "";
      }

      try {
        await onImported?.();
      } catch {
        toast.warning(
          "Las cuentas se guardaron. Actualiza el listado."
        );
      }
    } catch (error) {
      // Requiere validar otra vez antes de reintentar.
      setResult(null);
      toast.error(getErrorMessage(error));
    } finally {
      operationLock.current = false;
      setBusy(false);
    }
  };

  return (
    <section className="accounting-excel-panel">
      <div className="accounting-account-actions">
        <button
          type="button"
          className="btn-secondary"
          disabled={busy}
          onClick={() => download("export")}
        >
          Exportar Excel
        </button>

        <button
          type="button"
          className="btn-secondary"
          disabled={busy}
          onClick={() => download("template")}
        >
          Descargar plantilla
        </button>
      </div>

      <div className="accounting-excel-upload">
        <label>
          Importar cuentas desde Excel
          <input
            ref={inputRef}
            type="file"
            accept=".xlsx"
            onChange={selectFile}
            disabled={busy}
          />
        </label>

        <button
          type="button"
          className="btn-secondary"
          disabled={busy || !file}
          onClick={() => processFile(true)}
        >
          Validar archivo
        </button>

        <button
          type="button"
          className="btn-primary"
          disabled={busy || !file || !result?.valid}
          onClick={() => processFile(false)}
        >
          {busy ? "Procesando..." : "Importar cuentas"}
        </button>
      </div>

      <p>
        Formato .xlsx · Máximo 5 MB y 5000 cuentas.
        Los códigos deben estar guardados como texto.
      </p>

      {result && (
        <div aria-live="polite">
          <h3>
            {result.valid
              ? `${result.total} cuentas listas para importar`
              : "El archivo contiene errores"}
          </h3>

          {result.errors?.length > 0 && (
            <div className="accounting-table-wrapper">
              <table className="accounting-table">
                <thead>
                  <tr>
                    <th>Fila</th>
                    <th>Error</th>
                  </tr>
                </thead>

                <tbody>
                  {result.errors.map((error, index) => (
                    <tr key={`${error.fila}-${index}`}>
                      <td>{error.fila || "Archivo"}</td>
                      <td>{error.mensaje}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {result.valid && (
            <div className="accounting-table-wrapper">
              <table className="accounting-table">
                <thead>
                  <tr>
                    <th>Fila</th>
                    <th>Código</th>
                    <th>Nombre</th>
                    <th>Cuenta superior</th>
                    <th>Tipo</th>
                  </tr>
                </thead>

                <tbody>
                  {(result.preview || []).map((account) => (
                    <tr key={account.codigo}>
                      <td>{account.fila}</td>
                      <td>{account.codigo}</td>
                      <td>{account.nombre}</td>
                      <td>{account.codigo_padre || "—"}</td>
                      <td>{account.tipo}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </section>
  );
}